import Head from "next/head";
import { PREFERRED_URL, ENV } from "@/src/const";
import { SEOLogic } from "@/src/logic";
import { StrapiSingleType, StrapiCollectionType } from "@/src/type";
import { ProviderHooks } from "@/src/hook";

const TITLE = "Flow";
const DESCRIPTION = "Communities designed to connect you with yourself, your neighbors and nature.";
const DEFAULT_IMAGE = "https://flowhouse.imgix.net/cms/Flow_House_Icon_72fd2a03d6.png";
const FAV_ICON = DEFAULT_IMAGE;

interface SEOModuleProps {
  title?: string;
  byName?: string;
  pageDescription?: string;
  description?: string;
  domainImageURL?: string;
  pageImageURL?: string;
  schemaOverride?: any;
  path: string;
  webConfig: StrapiSingleType.WebConfig;
  keywords?: Array<string>;
  domain?: string;
  favIcon?: string;
  property?: StrapiCollectionType.Property;
  canonical?: string;
  pathOverride?: string;
}

function SEO({
  title = TITLE,
  byName,
  pageDescription,
  description = DESCRIPTION,
  domainImageURL = DEFAULT_IMAGE,
  pageImageURL,
  path = "",
  webConfig,
  keywords,
  domain,
  favIcon,
  schemaOverride,
  property,
  canonical,
  pathOverride,
}: SEOModuleProps) {
  const cms = ProviderHooks.useCMS();
  const _path = SEOLogic.getHomeCanonical(pathOverride ?? path, cms.locale, property?.slug);
  const _canonical = (canonical ?? _path === "/") ? "" : cms.constructLink(_path);
  let desc = pageDescription ?? property?.description ?? description;
  if (!description || description === "null") {
    desc = DESCRIPTION;
  }

  const mainImage = pageImageURL ?? property?.defaultImage?.data?.attributes?.url ?? domainImageURL;
  const finalByName = property?.name ?? byName ?? webConfig?.name ?? "";
  const sizedImageUrl = `${mainImage}?w=600&auto=format`;
  const url = (domain ? `https://${domain}` : undefined) ?? PREFERRED_URL;

  return (
    <Head>
      <title>{`${title}`}</title>
      <meta
        name="description"
        content={desc}
      />
      {Array.isArray(keywords) && (keywords ?? []).length > 0 && (
        <meta
          name="keywords"
          content={keywords.join(",")}
        />
      )}
      <meta
        name="author"
        content={finalByName}
      />
      <meta
        property="og:type"
        content="website"
      />
      <meta
        property="og:title"
        content={title}
      />
      <meta
        property="og:description"
        content={desc}
      />
      <meta
        property="og:image"
        content={sizedImageUrl}
      />
      <meta
        property="og:site_name"
        content={finalByName}
      />
      <meta
        name="twitter:card"
        content="summary_large_image"
      />
      <meta
        name="twitter:title"
        content={`${title}`}
      />
      <meta
        name="twitter:description"
        content={desc}
      />
      <meta
        name="twitter:image"
        content={sizedImageUrl}
      />
      <link
        rel="canonical"
        hrefLang="en"
        href={`${url}${_canonical}`}
      />
      <link
        rel="alternate"
        hrefLang="en"
        href={`${url}${_canonical}`}
      />
      <link
        rel="og:url"
        hrefLang="en"
        href={`${url}${_canonical}`}
      />
      <link
        rel="twitter:url"
        hrefLang="en"
        href={`${url}${_canonical}`}
      />
      <link
        rel="icon"
        href={favIcon ?? FAV_ICON}
      ></link>
      {ENV === "prod" ? (
        <meta
          name="robots"
          content="index,follow"
        />
      ) : (
        <meta
          name="robots"
          content="noindex, nofollow"
        />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            schemaOverride ??
              SEOLogic.getSchema({
                name: finalByName,
                headline: title,
                image: mainImage,
                description: desc,
                url: `${url}${_canonical}`,
                domain: url ?? PREFERRED_URL,
                property: property,
              })
          ),
        }}
      />
      <link
        rel="apple-touch-icon"
        href={`${favIcon ?? FAV_ICON}?w=192`}
      />
      <link
        rel="apple-touch-startup-image"
        href={favIcon ?? FAV_ICON}
      />
      <meta
        name="mobile-web-app-capable"
        content="yes"
      />
      <meta
        name="apple-mobile-web-app-status-bar-style"
        content="black"
      />
    </Head>
  );
}

export default SEO;
